import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const EvidenceCompiler = ({ claimType, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    companyName: 'Global Carbon Corp',
    reportingYear: '2024',
    baselineYear: '2020',
    sbtiTarget: true,
    sbtiTargetDate: '2030-12-31',
    emissionInventory: '',
    reductionEvidence: '',
    contributionStatement: '',
    additionalityEvidence: '',
    thirdPartyVerification: false,
    publicDisclosure: true
  });

  const [uploadedFiles, setUploadedFiles] = useState([
    {
      id: 1,
      name: 'GHG_Inventory_2024.pdf',
      type: 'emission-inventory',
      size: '2.4 MB',
      uploadDate: '2024-09-20',
      status: 'verified'
    },
    {
      id: 2,
      name: 'SBTi_Target_Validation.pdf',
      type: 'sbti-evidence',
      size: '1.8 MB',
      uploadDate: '2024-09-19',
      status: 'verified'
    }
  ]);

  const evidenceRequirements = {
    offsetting: [
      { id: 'emission-inventory', label: 'Comprehensive GHG Inventory', required: true },
      { id: 'sbti-evidence', label: 'SBTi Target Validation', required: true },
      { id: 'reduction-evidence', label: 'Emission Reduction Evidence', required: true },
      { id: 'residual-calculation', label: 'Residual Emissions Calculation', required: true }
    ],
    contribution: [
      { id: 'contribution-statement', label: 'Contribution Statement', required: true },
      { id: 'additionality-evidence', label: 'Additionality Evidence', required: true },
      { id: 'impact-assessment', label: 'Climate Impact Assessment', required: false },
      { id: 'transparency-report', label: 'Transparency Report', required: true }
    ],
    inset: [
      { id: 'value-chain-mapping', label: 'Value Chain Mapping', required: true },
      { id: 'intervention-evidence', label: 'Direct Intervention Evidence', required: true },
      { id: 'scope3-inventory', label: 'Scope 3 Inventory Alignment', required: true },
      { id: 'monitoring-plan', label: 'Monitoring & Verification Plan', required: true }
    ]
  };

  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: (2024 - i)?.toString(),
    label: (2024 - i)?.toString()
  }));

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (evidenceType) => {
    // Mock file upload
    const newFile = {
      id: Date.now(),
      name: `Evidence_${evidenceType}_${Date.now()}.pdf`,
      type: evidenceType,
      size: '1.2 MB',
      uploadDate: new Date()?.toISOString()?.split('T')?.[0],
      status: 'processing'
    };
    
    setUploadedFiles(prev => [...prev, newFile]);
    
    // Simulate processing
    setTimeout(() => {
      setUploadedFiles(prev => 
        prev?.map(file => 
          file?.id === newFile?.id 
            ? { ...file, status: 'verified' }
            : file
        )
      );
    }, 2000);
  };

  const getFileStatusIcon = (status) => {
    switch (status) {
      case 'verified': return { icon: 'CheckCircle', color: 'text-success' };
      case 'processing': return { icon: 'Clock', color: 'text-warning' };
      case 'error': return { icon: 'XCircle', color: 'text-error' };
      default: return { icon: 'FileText', color: 'text-muted-foreground' };
    }
  };

  const currentRequirements = evidenceRequirements?.[claimType] || [];
  const requiredCount = currentRequirements?.filter(req => req?.required)?.length;
  const uploadedCount = uploadedFiles?.filter(file => file?.status === 'verified')?.length;
  const completionRate = Math.round((uploadedCount / requiredCount) * 100);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-card-foreground mb-2">Evidence Compilation</h2>
        <p className="text-sm text-muted-foreground">
          Provide supporting evidence and documentation for your {claimType} claim. All required documents must be uploaded and verified.
        </p>
      </div>
      {/* Progress Indicator */}
      <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-card-foreground">Evidence Completion</span>
          <span className="text-sm font-medium text-primary">{completionRate}%</span>
        </div>
        <div className="w-full bg-border rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionRate}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{uploadedCount} of {requiredCount} required documents</span>
          <span>{uploadedFiles?.length} total files</span>
        </div>
      </div>
      {/* Company Information */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-4">Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            value={formData?.companyName}
            onChange={(e) => handleInputChange('companyName', e?.target?.value)}
            required
          />
          <Select
            label="Reporting Year"
            options={yearOptions}
            value={formData?.reportingYear}
            onChange={(value) => handleInputChange('reportingYear', value)}
            required
          />
          <Select
            label="Baseline Year"
            options={yearOptions}
            value={formData?.baselineYear}
            onChange={(value) => handleInputChange('baselineYear', value)}
            required
          />
          <Input
            label="SBTi Target Date"
            type="date"
            value={formData?.sbtiTargetDate}
            onChange={(e) => handleInputChange('sbtiTargetDate', e?.target?.value)}
            required={claimType === 'offsetting'}
          />
        </div>
      </div>
      {/* Evidence Requirements */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-4">Evidence Requirements</h3>
        <div className="space-y-4">
          {currentRequirements?.map((requirement) => {
            const hasFile = uploadedFiles?.some(file => 
              file?.type === requirement?.id && file?.status === 'verified'
            );
            
            return (
              <div key={requirement?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={hasFile ? 'CheckCircle' : 'Circle'} 
                      size={16} 
                      className={hasFile ? 'text-success' : 'text-muted-foreground'} 
                    />
                    <span className="font-medium text-card-foreground">{requirement?.label}</span>
                    {requirement?.required && (
                      <span className="text-xs bg-error text-error-foreground px-2 py-1 rounded">
                        Required
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFileUpload(requirement?.id)}
                    iconName="Upload"
                    iconPosition="left"
                  >
                    Upload
                  </Button>
                </div>
                {/* Show uploaded files for this requirement */}
                {uploadedFiles?.filter(file => file?.type === requirement?.id)?.map(file => {
                    const statusConfig = getFileStatusIcon(file?.status);
                    return (
                      <div key={file?.id} className="flex items-center space-x-3 mt-2 p-2 bg-muted/30 rounded">
                        <Icon name={statusConfig?.icon} size={16} className={statusConfig?.color} />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-card-foreground">{file?.name}</div>
                          <div className="text-xs text-muted-foreground">{file?.size} • {file?.uploadDate}</div>
                        </div>
                        <Button variant="ghost" size="sm" iconName="X" />
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      </div>
      {/* Verification Options */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-4">Verification & Disclosure</h3>
        <div className="space-y-4">
          <Checkbox
            label="Third-party verification requested"
            description="Request independent verification of claim documentation"
            checked={formData?.thirdPartyVerification}
            onChange={(e) => handleInputChange('thirdPartyVerification', e?.target?.checked)}
          />
          <Checkbox
            label="Public disclosure authorized"
            description="Allow public disclosure of claim details for transparency"
            checked={formData?.publicDisclosure}
            onChange={(e) => handleInputChange('publicDisclosure', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Validation Messages */}
      {completionRate < 100 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-warning mb-1">Incomplete Evidence</h4>
              <p className="text-xs text-warning/80">
                Please upload all required documents before proceeding. Missing documents may delay claim processing.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} iconName="ArrowLeft" iconPosition="left">
          Back to Credits
        </Button>
        <Button
          onClick={onNext}
          disabled={completionRate < 100}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Review & Submit
        </Button>
      </div>
    </div>
  );
};

export default EvidenceCompiler;